import { useState } from "react";
import { FlaskConical, Cpu, Wifi, TrendingUp } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { ScenePillar } from "../shared/Scene";

const items = [
  {
    id: "aerolab",
    icon: FlaskConical,
    label: "AeroLab Encoding",
    caption: "TPN Gold, Mumbai",
    detail: "$30 per movie · $10 per TV title · $10 per audio — same rate across independents, TV, audio, and Warner Bros / Disney catalogues.",
  },
  {
    id: "ai",
    icon: Cpu,
    label: "AI-Assisted Tools",
    caption: "Metadata, compliance, curation",
    detail: "Metadata localisation, OEM output automation, agentic data analysis and recommendations — curation at scale.",
  },
  {
    id: "wireless",
    icon: Wifi,
    label: "Wireless Options",
    caption: "Low-cost onboard",
    detail: "Onboard wireless device and IFE platform from $250/month per tail.",
  },
  {
    id: "maxposure",
    icon: TrendingUp,
    label: "Advertising Ecosystem",
    caption: "Ancillary media revenue",
    detail: "Advertising, sponsorships, inflight magazine, digital content and brand activations — a monetization layer built for airlines powered by Maxposure and APX Luxury.",
  },
];

const SlideLean = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const [active, setActive] = useState(null);
  const lean = airline?.content?.lean;
  const rows = lean
    ? items.map((it) => (it.id === "aerolab" ? { ...it, detailHeading: lean.aerolab.heading, detail: lean.aerolab.detail } : it))
    : items;

  const tile = isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10";
  const gridVisual = (
    <div className="grid grid-cols-2 gap-3" data-testid="lean-grid">
      {rows.map((it, i) => {
        const Icon = it.icon;
        const isOn = active === i;
        return (
          <button
            key={it.id}
            onClick={() => setActive(isOn ? null : i)}
            data-testid={`lean-item-${it.id}`}
            aria-pressed={isOn}
            className={`relative min-h-[170px] rounded-2xl border p-5 text-left transition-colors ${tile}`}
            style={isOn ? { borderColor: "#10B981" } : undefined}
          >
            {isOn ? (
              <div data-testid={`lean-detail-${it.id}`} style={{ animation: "fadeIn 0.25s ease-out" }}>
                <span className="block k-eyebrow mb-2" style={{ color: "#10B981" }}>{it.detailHeading || it.label}</span>
                <p className={`text-[14px] leading-relaxed ${isDark ? "text-white/80" : "text-black/70"}`}>{it.detail}</p>
              </div>
            ) : (
              <>
                <span className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-white/5 text-white" : "bg-black/5 text-[#1d1d1f]"}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className={`mt-4 text-[16px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{it.label}</div>
                <div className={`mt-1 text-[13px] ${isDark ? "text-white/50" : "text-black/50"}`}>{it.caption}</div>
              </>
            )}
          </button>
        );
      })}
    </div>
  );

  const visual = lean?.grid ? gridVisual : (
    <div className={`rounded-2xl p-6 ${isDark ? "bg-white/[0.03] border border-white/10" : "bg-white border border-black/10"}`}>
      <ul className={`divide-y ${isDark ? "divide-white/10" : "divide-black/10"}`}>
        {rows.map((it, i) => {
          const Icon = it.icon;
          const isOn = active === i;
          return (
            <li key={it.id}>
              <button
                onClick={() => setActive(isOn ? null : i)}
                data-testid={`lean-item-${it.id}`}
                className={`w-full flex items-center gap-4 py-3.5 text-left group transition-colors ${
                  isDark ? "hover:text-white" : "hover:text-[#1d1d1f]"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                    isOn ? "" : isDark ? "bg-white/5" : "bg-black/5"
                  }`}
                  style={isOn ? { backgroundColor: "#10B981", color: "#0b0b0d" } : { color: isDark ? "#f5f5f7" : "#1d1d1f" }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{it.label}</div>
                  <div className={`text-[13px] ${isDark ? "text-white/50" : "text-black/50"}`}>{it.caption}</div>
                </div>
                <span className={`text-[13px] font-medium transition-transform ${isOn ? "rotate-180" : ""} ${isDark ? "text-white/45" : "text-black/45"}`}>▾</span>
              </button>
              {isOn && (
                <p className={`pb-4 pl-13 text-[14px] leading-relaxed ${isDark ? "text-white/70" : "text-black/65"}`} data-testid={`lean-detail-${it.id}`}>
                  {it.detailHeading && (
                    <span className="block k-eyebrow mb-1" style={{ color: "#10B981" }}>{it.detailHeading}</span>
                  )}
                  {it.detail}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <ScenePillar
      eyebrow="Lean — Platform"
      headline="Same quality."
      accentLine="Less spend."
      accent="#10B981"
      lead="AI-assisted workflows and shared infrastructure keep total cost of IFE down — without pulling on your team."
      visual={visual}
      outcomes={[
        { label: "Encoding, per title", value: lean?.encodingOutcome || "$30" },
        { label: "Wireless / month / tail", value: "$250" },
      ]}
    />
  );
};

export default SlideLean;
